<template>
  <div class="room-selection">
    <!-- Header -->
    <div class="header-section">
      <div class="pre-title">MUIC ORIENTATION</div>
      <h1 class="main-title">Choose your Sign in type</h1>
      <p class="subtitle">
        Select a verified account type to continue.
      </p>
    </div>

    <!-- Selection Card -->
    <div class="selection-card">
      <div class="card-header">
        <span class="card-title">ACCOUNT TYPE</span>
        <span class="room-count">{{ filteredRooms.length }}</span>
      </div>

      <div class="input-group">
        <!-- <label style="display: block; margin-bottom: 12px; font-weight: 600; color: #555; font-size: 16px;">Choose a sign in type</label> -->
        <div class="plan-cards">
          <div v-for="room in filteredRooms" :key="room.public_id" class="plan-card"
            :class="{ 'active': selectedRoomId === room.public_id }" @click="selectedRoomId = room.public_id">

            <div class="plan-radio-btn" :class="{ 'active': selectedRoomId === room.public_id }"></div>

            <div class="plan-icon">
              <!-- Different icons depending on provider -->
              <a-icon v-if="room.provider === 'muic'" type="safety-certificate"
                :style="{ color: selectedRoomId === room.public_id ? '#0070f3' : '#472d8a' }" />
              <a-icon v-else type="idcard"
                :style="{ color: selectedRoomId === room.public_id ? '#0070f3' : '#e67e22' }" />
            </div>

            <div class="plan-details">
              <div class="plan-title">
                {{ room.provider === 'muic' ? 'MUIC Account' : (room.provider === 'mu' ? 'MU Account' :
                  room.provider.toUpperCase()) }}
              </div>
              <div class="plan-desc">
                <div class="room-tag">{{ room.room_name }}</div>
                <span v-if="room.provider === 'muic'">For students with <br /><strong>@muic.io</strong> email.</span>
                <span v-else-if="room.provider === 'mu'">For students with <br /><strong>@student.mahidol</strong>
                  email.</span>
              </div>
            </div>
          </div>
        </div>
      </div><!-- Steps Box -->
      <div class="steps-info-box">
        <div v-if="selectedAccountType === 'MUIC Account'">
          <ul class="steps-list">
            <li>Scan the room QR code.</li>
            <li>Sign in with a verified <span class="highlight-yellow">uXXXXXXX@muic.io</span> Microsoft account.</li>
            <li>The system derives the Student ID from the email address.</li>
            <li>The Student ID is checked against the roster.</li>
            <li>Attendance and prize eligibility are confirmed.</li>
          </ul>
          <div class="example-text">Example: u69800001@muic.io becomes Student ID 69800001.</div>
        </div>

        <div v-else-if="selectedAccountType === 'MU Account'">
          <ul class="steps-list">
            <li>Scan the room QR code.</li>
            <li>Sign in with a verified <span class="highlight-yellow">@student.mahidol.ac.th</span> or <span
                class="highlight-yellow">@mahidol.ac.th</span> Microsoft account.</li>
            <li>Enter the Student ID after Microsoft sign-in.</li>
            <li>The Student ID is checked against the roster.</li>
            <li>Attendance and prize eligibility are confirmed.</li>
          </ul>
          <div class="example-text">MU email is optional in the roster because it may not be known in advance. The
            Microsoft identity and the entered Student ID are still limited to one check-in each per room.</div>
        </div>
      </div>

      <!-- Action Button -->
      <button class="continue-btn" @click="handleContinue">
        Continue to check-in
        <a-icon type="export" style="margin-left: 8px;" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoomSelection',
  props: {
    initialAccountType: {
      type: String,
      default: 'MUIC Account'
    },
    rooms: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedRoomId: null,
    };
  },
  computed: {
    filteredRooms() {
      const typeQuery = this.$route.query.type;
      if (!typeQuery || !this.rooms) return this.rooms || [];
      const providerTarget = typeQuery === 'MUAccount' ? 'mu' : 'muic';
      return this.rooms.filter(r => r.provider === providerTarget);
    },
    selectedAccountType() {
      if (!this.rooms || this.rooms.length === 0) return 'MUIC Account';
      const selectedRoom = this.rooms.find(r => r.public_id === this.selectedRoomId);
      if (selectedRoom) {
        return selectedRoom.provider === 'mu' ? 'MU Account' : 'MUIC Account';
      }
      return 'MUIC Account';
    }
  },
  mounted() {
    // If no type in query, default to MUICAccount
    if (!this.$route.query.type) {
      this.$router.replace({ query: { ...this.$route.query, type: 'MUICAccount' } }).catch(() => {});
    }
  },
  watch: {
    rooms: {
      immediate: true,
      handler(newRooms) {
        this.syncWithQuery(this.$route.query.type, newRooms);
      }
    },
    '$route.query.type': {
      immediate: true,
      handler(newType) {
        this.syncWithQuery(newType, this.rooms);
      }
    },
    selectedRoomId(newVal) {
      if (newVal) {
        const selectedRoom = this.rooms.find(r => r.public_id === newVal);
        if (selectedRoom) {
          const typeStr = selectedRoom.provider === 'mu' ? 'MUAccount' : 'MUICAccount';
          if (this.$route.query.type !== typeStr) {
             this.$router.replace({ query: { ...this.$route.query, type: typeStr } }).catch(() => {});
          }
        }
      }
    }
  },
  methods: {
    syncWithQuery(typeQuery, rooms) {
      if (rooms && rooms.length > 0) {
        const providerTarget = typeQuery === 'MUAccount' ? 'mu' : 'muic';
        const match = rooms.find(r => r.provider === providerTarget);
        if (match && this.selectedRoomId !== match.public_id) {
          this.selectedRoomId = match.public_id;
        } else if (!match && !this.selectedRoomId) {
          this.selectedRoomId = rooms[0].public_id;
        }
      }
    },
    handleContinue() {
      // Emit event to parent to switch view and pass the selected account type and room id
      this.$emit('continue', { type: this.selectedAccountType, roomId: this.selectedRoomId });
    }
  }
};
</script>

<style scoped>
.room-selection {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Header Section */
.header-section {
  text-align: left;
  margin-bottom: 30px;
  padding: 0 20px;
}

.pre-title {
  color: #3e247a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.main-title {
  color: #3e247a;
  font-size: 48px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 16px;
  letter-spacing: -1.5px;
}

.subtitle {
  color: #6c757d;
  font-size: 15px;
  line-height: 1.5;
}

/* Card Container */
.selection-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-title {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  letter-spacing: 1px;
}

.room-count {
  background: #f0ebf8;
  color: #3e247a;
  font-size: 12px;
  font-weight: 700;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Select Wrapper */
/* Plan Cards (Pricing Plan Style) */
.plan-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.plan-card {
  flex: 1;
  border: 1px solid #d9d9d9;
  border-radius: 12px;
  padding: 24px 20px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  background: white;
}

.plan-card:hover {
  border-color: #b0c4de;
}

.plan-card.active {
  border-color: #0070f3;
  background-color: #f0f7ff;
}

.plan-radio-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d9d9d9;
  background: white;
  transition: all 0.2s ease;
}

.plan-radio-btn.active {
  border-color: #0070f3;
  border-width: 5px;
}

.plan-icon {
  font-size: 42px;
  margin-right: 16px;
}

.plan-details {
  flex: 1;
}

.plan-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}

.plan-card.active .plan-title {
  color: #111;
}

.plan-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}

.room-tag {
  display: inline-block;
  background: #eaeaea;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 6px;
}

.plan-card.active .room-tag {
  background: #dbeafe;
  color: #0070f3;
}

/* Steps Box */
.steps-info-box {
  background: #fdfdfd;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  color: #555;
  font-size: 13px;
  line-height: 1.6;
}

.steps-list {
  padding-left: 20px;
  margin-bottom: 12px;
}

.steps-list li {
  margin-bottom: 6px;
}

.example-text {
  font-size: 11px;
  color: #888;
  background: #f4f4f4;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #3e247a;
}

/* Action Button */
.continue-btn {
  width: 100%;
  background: #472c82;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.continue-btn:hover {
  background: #361f63;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .main-title {
    font-size: 38px;
  }

  .selection-card {
    padding: 24px 20px;
  }

  .plan-cards {
    flex-direction: column;
  }
}

@media (max-height: 750px) {
  .main-title {
    font-size: 32px;
  }

  .selection-card {
    padding: 20px;
  }
}
</style>
